import {
  getDPIThreshold,
  isArtPhoto,
  isCenterpiece,
  isCollage,
} from '@mixtiles/web-backend-shared'
import { type Tile } from '../components/TilesProvider/TilesProvider.types'
import {
  getUpscaledMegapixels,
  getTileDPI,
  isFixableByUpscale,
  type Face,
} from '../services/Analytics/dpiMetrics'

type UpscaleEligibilityFactors = {
  isCollage: boolean
  isCenterpiece: boolean
  isArtPhoto: boolean
  dpi: number | null
  dpiThreshold: number
  megapixels: number
  faceCount: number
  smallestFaceAreaPct: number | null
  tileSize: string
  materialType: string
  matting: string
  isFixableByUpscale: boolean
  upscaledMegapixels: number
}

type UpscaleEligibilityResult = {
  eligible: boolean
  reason:
    | 'no_face_high_res'
    | 'face_large_enough'
    | 'not_eligible_low_mp'
    | 'not_eligible_small_face'
    | 'not_eligible_high_dpi'
    | 'not_eligible_unsupported_tile_type'
    | 'null_dpi_at_calculation'
  factors: UpscaleEligibilityFactors
}

const MIN_MEGAPIXELS_WITH_FACES = 2
const MIN_MEGAPIXELS_NO_FACES = 1
const MIN_FACE_AREA_PCT = 3

function getUpscaleFactors(
  tile: Tile & { faces?: Face[] }
): UpscaleEligibilityFactors {
  const { width: tileWidth, height: tileHeight } = tile
  const fullImageArea = tileWidth * tileHeight
  const faces = tile.faces
  const faceCount = faces?.length ?? 0

  let smallestFaceAreaPct: number | null = null
  if (faceCount > 0) {
    const faceAreas = faces!.map((face) => {
      return face.width * tileWidth * (face.height * tileHeight)
    })
    smallestFaceAreaPct = (Math.min(...faceAreas) / fullImageArea) * 100
  }

  return {
    isCollage: isCollage(tile),
    isCenterpiece: isCenterpiece(tile),
    isArtPhoto: isArtPhoto(tile),
    dpi: getTileDPI(tile),
    dpiThreshold: getDPIThreshold(tile.tileSize),
    megapixels: fullImageArea / 1_000_000,
    faceCount,
    smallestFaceAreaPct,
    tileSize: tile.tileSize,
    materialType: tile.materialType,
    matting: tile.customStyle?.matting,
    isFixableByUpscale: isFixableByUpscale(tile, 3) === true,
    upscaledMegapixels: getUpscaledMegapixels(tile),
  }
}

export function computeUpscaleEligibility(
  tile: Tile & { faces?: Face[] }
): UpscaleEligibilityResult {
  const factors = getUpscaleFactors(tile)
  const { dpi, dpiThreshold, isCenterpiece, isCollage, isArtPhoto } = factors

  const { megapixels, faceCount, smallestFaceAreaPct } = factors

  if (isCollage || isCenterpiece || isArtPhoto) {
    return {
      eligible: false,
      reason: 'not_eligible_unsupported_tile_type',
      factors,
    }
  }

  if (dpi === null) {
    return { eligible: false, reason: 'null_dpi_at_calculation', factors }
  }

  if (dpi >= dpiThreshold) {
    return { eligible: false, reason: 'not_eligible_high_dpi', factors }
  }

  if (faceCount === 0) {
    if (megapixels <= MIN_MEGAPIXELS_NO_FACES) {
      return { eligible: false, reason: 'not_eligible_low_mp', factors }
    }
    return { eligible: true, reason: 'no_face_high_res', factors }
  }

  if (megapixels <= MIN_MEGAPIXELS_WITH_FACES) {
    return { eligible: false, reason: 'not_eligible_low_mp', factors }
  }

  if (smallestFaceAreaPct! >= MIN_FACE_AREA_PCT) {
    return { eligible: true, reason: 'face_large_enough', factors }
  }

  return { eligible: false, reason: 'not_eligible_small_face', factors }
}
